import { toast } from "@devhop/ui";
import { formatCfApiErrorMessage } from "@/shared/lib/cf-api-error-messages";
import {
  shouldBlockCfApiRequests,
  showCfAuthExpiredDialog,
} from "@/shared/lib/cf-auth-session";

type ApiErrorShape = {
  message?: string;
  error?: string;
  code?: string;
} | null;

/** API error ที่ handleError จัดการ toast แล้ว — ไม่ toast ซ้ำใน requestJson */
export class ApiRequestError extends Error {
  readonly code: string;
  readonly status: number;
  readonly handled = true;

  constructor(code: string, status: number) {
    super(code);
    this.name = "ApiRequestError";
    this.code = code;
    this.status = status;
  }
}

export type FetcherRequestOptions = {
  /** ไม่แสดง toast จาก fetcher — ให้ caller (mutation) จัดการเอง */
  silentError?: boolean;
};

async function parseJsonSafe(res: Response): Promise<ApiErrorShape> {
  try {
    return await res.clone().json();
  } catch {
    return null;
  }
}

export async function handleError(
  res: Response,
  options?: FetcherRequestOptions,
): Promise<never> {
  const data = await parseJsonSafe(res);
  const status = res.status;
  const code =
    (data && (data.code || data.message || data.error)) ||
    (status === 401
      ? "UNAUTHORIZED"
      : status === 403
        ? "FORBIDDEN"
        : status >= 500
          ? "SERVER_ERROR"
          : "REQUEST_FAILED");

  const userMessage =
    typeof data?.message === "string" && data.message.trim()
      ? data.message.trim()
      : formatCfApiErrorMessage(String(code));

  if (!options?.silentError) {
    if (status === 401) {
      void showCfAuthExpiredDialog({ message: userMessage });
    } else if (status === 403) {
      console.error(userMessage);
    } else if (status >= 400 && status < 500) {
      toast.error(userMessage);
    } else {
      toast.error(userMessage);
    }
  }

  throw new ApiRequestError(String(code), status);
}

async function requestJson<TResponse>(
  url: string,
  init: RequestInit,
  options?: FetcherRequestOptions,
): Promise<TResponse> {
  if (shouldBlockCfApiRequests()) {
    throw new ApiRequestError("UNAUTHORIZED", 401);
  }
  try {
    const res = await fetch(url, init);
    if (!res.ok) return handleError(res, options);
    return (await res.json()) as TResponse;
  } catch (e) {
    if (e instanceof ApiRequestError) throw e;
    toast.error("ເຄືອຂ່າຍມີບັນຫາ", {
      description: "ກະລຸນາກວດກາໄຟອິນເຕີເນັດຂອງທ່ານ.",
    });
    throw e;
  }
}

export const fetcher = {
  get: async <TResponse>(
    url: string,
    options?: FetcherRequestOptions,
  ): Promise<TResponse> => {
    return requestJson<TResponse>(url, { credentials: "include" }, options);
  },
  postForm: async <TResponse>(
    url: string,
    body: FormData,
  ): Promise<TResponse> => {
    return requestJson<TResponse>(url, {
      method: "POST",
      body,
      credentials: "include",
    });
  },
  post: async <TResponse>(
    url: string,
    body?: unknown,
    options?: FetcherRequestOptions,
  ): Promise<TResponse> => {
    return requestJson<TResponse>(
      url,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body ?? {}),
        credentials: "include",
      },
      options,
    );
  },
  postWithHeaders: async <TResponse>(
    url: string,
    body: unknown,
    headers: Record<string, string>,
    options?: FetcherRequestOptions,
  ): Promise<TResponse> => {
    return requestJson<TResponse>(
      url,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify(body ?? {}),
        credentials: "include",
      },
      options,
    );
  },
  put: async <TResponse>(url: string, body?: unknown): Promise<TResponse> => {
    return requestJson<TResponse>(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body ?? {}),
      credentials: "include",
    });
  },
  putForm: async <TResponse>(
    url: string,
    body: FormData,
  ): Promise<TResponse> => {
    return requestJson<TResponse>(url, {
      method: "PUT",
      body,
      credentials: "include",
    });
  },
  patch: async <TResponse>(url: string, body?: unknown): Promise<TResponse> => {
    return requestJson<TResponse>(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body ?? {}),
      credentials: "include",
    });
  },
  patchWithHeaders: async <TResponse>(
    url: string,
    body: unknown,
    headers: Record<string, string>,
    options?: FetcherRequestOptions,
  ): Promise<TResponse> => {
    return requestJson<TResponse>(
      url,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify(body ?? {}),
        credentials: "include",
      },
      options,
    );
  },
  patchForm: async <TResponse>(
    url: string,
    body: FormData,
  ): Promise<TResponse> => {
    return requestJson<TResponse>(url, {
      method: "PATCH",
      body,
      credentials: "include",
    });
  },
  delete: async <TResponse>(url: string): Promise<TResponse> => {
    return requestJson<TResponse>(url, {
      method: "DELETE",
      credentials: "include",
    });
  },
};
